import { Head, Link } from "@inertiajs/react"
import { ArrowLeft, ExternalLink } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"

type Check = {
  key: string
  label: string
  status: "pass" | "warn" | "fail"
  finding: string
  detail?: string
}

type Audit = {
  id: number
  token: string
  url: string
  email: string
  business_name: string | null
  status: string
  grade: string | null
  score: number | null
  created_at: string
  referrer: string | null
  utm_source: string | null
  results: {
    final_url?: string | null
    response_time_ms?: number | null
    html_bytes?: number | null
    checks?: Check[]
  }
}

const STATUS_ORDER = { fail: 0, warn: 1, pass: 2 } as const

export default function AdminSeoAuditShow({ audit }: { audit: Audit }) {
  const checks = [...(audit.results.checks ?? [])].sort(
    (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
  )

  return (
    <>
      <Head title={`Audit — ${audit.url}`}>
        <meta name="description" content="SEO audit lead detail." />
        <meta property="og:title" content={`Audit — ${audit.url}`} />
        <meta property="og:description" content="SEO audit lead detail." />
      </Head>
      <AdminShell>
        <Link
          href="/admin/seo_audits"
          className="inline-flex items-center gap-1 text-sm text-ink-muted no-underline hover:text-ink-display"
        >
          <ArrowLeft className="h-4 w-4" /> All audits
        </Link>

        <div className="mt-4 border-b border-hairline pb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1>{audit.url}</h1>
              <p className="mt-1">
                <a href={`mailto:${audit.email}`} className="font-medium text-accent-display">
                  {audit.email}
                </a>
                {audit.business_name && <span className="text-ink-muted"> · {audit.business_name}</span>}
              </p>
              <p className="mt-1 text-sm text-ink-muted">
                Ran {formatDate(audit.created_at)}
                {audit.utm_source && ` · via ${audit.utm_source}`}
                {audit.referrer && ` · from ${audit.referrer}`}
              </p>
            </div>
            <div className="text-right">
              {audit.grade && (
                <span className="font-display text-4xl font-bold text-ink-display">{audit.grade}</span>
              )}
              {audit.score !== null && <p className="text-sm text-ink-muted">{audit.score}/100</p>}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <a
              href={`/seo/audit/${audit.token}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-ink-body no-underline hover:text-accent-display"
            >
              Public report <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>

        {checks.length === 0 ? (
          <p className="mt-8 text-ink-muted">No results yet — status: {audit.status}.</p>
        ) : (
          <div className="mt-6 space-y-3">
            {checks.map((check) => (
              <div key={check.key} className="rounded-lg border border-hairline bg-page p-4">
                <div className="flex items-start gap-3">
                  <CheckBadge status={check.status} />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-ink-display">{check.label}</p>
                    <p className="mt-0.5 text-sm text-ink-body">{check.finding}</p>
                    {check.detail && <p className="mt-1 text-xs text-ink-muted">{check.detail}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </AdminShell>
    </>
  )
}

function CheckBadge({ status }: { status: "pass" | "warn" | "fail" }) {
  if (status === "pass") return <Badge tone="signal">Pass</Badge>
  if (status === "warn") return <Badge tone="muted">Warn</Badge>
  return <Badge tone="accent">Fail</Badge>
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
